import React from "react";
import type { NewReportDataItem } from "../../../../api/client/types/new.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { PaginatedTable } from "../../../../shared/components/ui/PaginatedTable";
import { EditButton } from "../../../../shared/components/ui/EditButton";
import { type ColumnDef } from "@tanstack/react-table";
import { pluralize } from "../../../../shared/utils/stringUtils";
import { Trash2 } from "lucide-react";
import {
  formatDateAndTime,
  formatDateOnly,
} from "../../../../shared/utils/dateTimeFormat";

/**
 * Column minimum widths (px) for the news report preview table.
 * Adjust any value here to tweak a single column min-width.
 */
const COLUMN_MIN_WIDTHS_PX = {
  actions: 50,
  created_at: 50,
  date: 50,
  building_name: 160,
  lifeguard_name: 160,
  report_type_name: 160,
  news_type_name: 220,
  event_time: 80,
  intervention_level_name: 150,
  swimmer_name: 170,
  tower_apartment: 110,
  age_classification_name: 130,
  responsible_name: 170,
  event_description: 420,
  security_officer_name: 150,
  spent_items: 150,
  event_main_factor_name: 180,
  event_specific_factor_name: 220,
} as const;

interface NewReportPreviewTableProps {
  newsData: PaginatedResponse<NewReportDataItem>;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Callback when the user clicks the edit icon */
  onEditNew?: (item: NewReportDataItem) => void;
  onDeleteNew?: (newId: string) => void;
  isDeletingNew?: boolean;
  /** When true, draws borders around every table cell */
  showCellBorders?: boolean;
}

export const NewReportPreviewTable: React.FC<NewReportPreviewTableProps> = ({
  newsData,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditNew,
  onDeleteNew,
  isDeletingNew,
  showCellBorders,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = React.useRef<HTMLDivElement | null>(null);
  const scrollSyncSourceRef = React.useRef<"top" | "table" | null>(null);
  const [topScrollContentWidth, setTopScrollContentWidth] = React.useState(0);

  const handleEditClick = React.useCallback(
    (item: NewReportDataItem) => {
      onEditNew?.(item);
    },
    [onEditNew]
  );

  React.useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const topScrollbarElement = topScrollbarRef.current;

    if (!wrapperElement || !topScrollbarElement) {
      return;
    }

    const tableScrollElement = wrapperElement.querySelector(
      ".sticky-header-container"
    ) as HTMLDivElement | null;

    if (!tableScrollElement) {
      return;
    }

    const syncMetrics = () => {
      setTopScrollContentWidth(tableScrollElement.scrollWidth);
      if (scrollSyncSourceRef.current === null) {
        topScrollbarElement.scrollLeft = tableScrollElement.scrollLeft;
      }
    };

    const onTopScroll = () => {
      if (scrollSyncSourceRef.current === "table") {
        return;
      }

      scrollSyncSourceRef.current = "top";
      tableScrollElement.scrollLeft = topScrollbarElement.scrollLeft;
      requestAnimationFrame(() => {
        if (scrollSyncSourceRef.current === "top") {
          scrollSyncSourceRef.current = null;
        }
      });
    };

    const onTableScroll = () => {
      if (scrollSyncSourceRef.current === "top") {
        return;
      }

      scrollSyncSourceRef.current = "table";
      topScrollbarElement.scrollLeft = tableScrollElement.scrollLeft;
      requestAnimationFrame(() => {
        if (scrollSyncSourceRef.current === "table") {
          scrollSyncSourceRef.current = null;
        }
      });
    };

    topScrollbarElement.addEventListener("scroll", onTopScroll, { passive: true });
    tableScrollElement.addEventListener("scroll", onTableScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            syncMetrics();
          })
        : null;

    if (resizeObserver) {
      resizeObserver.observe(tableScrollElement);
      const tableElement = tableScrollElement.querySelector("table");
      if (tableElement) {
        resizeObserver.observe(tableElement);
      }
    }

    const intersectionObserver =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
                syncMetrics();
              }
            },
            { threshold: [0, 0.01] }
          )
        : null;

    if (intersectionObserver) {
      intersectionObserver.observe(wrapperElement);
    }

    window.addEventListener("resize", syncMetrics);
    requestAnimationFrame(syncMetrics);

    return () => {
      topScrollbarElement.removeEventListener("scroll", onTopScroll);
      tableScrollElement.removeEventListener("scroll", onTableScroll);
      window.removeEventListener("resize", syncMetrics);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      scrollSyncSourceRef.current = null;
    };
  }, [newsData?.items?.length, page, pageSize, isLoading, isError]);

  const columns = React.useMemo<ColumnDef<NewReportDataItem>[]>(
    () => [
      {
        header: "ACCIONES",
        id: "actions",
        cell: ({ row }) => (
          <div
            className="flex items-center justify-center gap-2"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.actions }}
          >
            <EditButton
              onClick={() => handleEditClick(row.original)}
              title="Editar novedad"
              iconSize={16}
            />
            <button
              onClick={() => onDeleteNew?.(row.original.new_id)}
              disabled={isDeletingNew}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eliminar novedad"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
      {
        header: () => (
          <span
            className="block bg-slate-300 text-slate-900 -mx-2.5 -my-2 px-2.5 py-2 min-h-full"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.created_at }}
            title="Fecha de registro en el sistema (no se modifica al editar la novedad)"
          >
            CREADO EL
          </span>
        ),
        accessorKey: "created_at",
        cell: ({ getValue }) => {
          const { datePart, formattedTime } = formatDateAndTime(getValue() as string);
          return (
            <div style={{ minWidth: COLUMN_MIN_WIDTHS_PX.created_at }}>
              <div className="whitespace-nowrap">{datePart}</div>
              {formattedTime ? <div className="whitespace-nowrap">{formattedTime}</div> : null}
            </div>
          );
        },
      },
      {
        header: "FECHA",
        accessorKey: "date",
        cell: ({ getValue }) => (
          <div
            className="whitespace-nowrap"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.date }}
          >
            {formatDateOnly(getValue() as string)}
          </div>
        ),
      },
      {
        header: "UNIDAD",
        accessorKey: "building_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.building_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "SALVAVIDAS DE TURNO",
        accessorKey: "lifeguard_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.lifeguard_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "TIPO DE NOVEDAD",
        accessorKey: "report_type_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.report_type_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "NOVEDAD",
        accessorKey: "news_type_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.news_type_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "HORA DE NOVEDAD",
        accessorKey: "event_time",
        cell: ({ getValue }) => (
          <div
            className="whitespace-nowrap"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.event_time }}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "NIVEL DE INTERVENCION",
        accessorKey: "intervention_level_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.intervention_level_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "NOMBRE DEL BANISTA",
        accessorKey: "swimmer_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.swimmer_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "TORRE/APTO",
        accessorKey: "tower_apartment",
        cell: ({ getValue }) => (
          <div
            className="whitespace-nowrap"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.tower_apartment }}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "EDAD",
        accessorKey: "age_classification_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.age_classification_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "ADULTO RESPONSABLE",
        accessorKey: "responsible_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.responsible_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "DESCRIPCION DE LOS HECHOS",
        accessorKey: "event_description",
        cell: ({ getValue }) => {
          const value = getValue() as string;
          return (
            <div
              className="whitespace-pre-wrap wrap-break-word"
              style={{ minWidth: COLUMN_MIN_WIDTHS_PX.event_description }}
              title={value}
            >
              {value}
            </div>
          );
        },
      },
      {
        header: "OFICIAL DE SEGURIDAD",
        accessorKey: "security_officer_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.security_officer_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "INSUMOS GASTADOS",
        accessorKey: "spent_items",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.spent_items }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "FACTOR GENERAL",
        accessorKey: "event_main_factor_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.event_main_factor_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "FACTOR ESPECÍFICO",
        accessorKey: "event_specific_factor_name",
        cell: ({ getValue }) => (
          <div
            className="line-clamp-4"
            style={{ minWidth: COLUMN_MIN_WIDTHS_PX.event_specific_factor_name }}
            title={getValue() as string}
          >
            {getValue() as string}
          </div>
        ),
      },
    ],
    [handleEditClick, onDeleteNew, isDeletingNew]
  );

  return (
    <div ref={wrapperRef} className="news-preview-top-scroll">
      <PaginatedTable<NewReportDataItem>
        data={newsData}
        columns={columns}
        afterTotalContent={
          <div
            ref={topScrollbarRef}
            className="lg:sticky lg:z-61"
            tabIndex={0}
            aria-label="Desplazamiento horizontal superior de tabla de novedades"
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: 16,
              top: "calc(var(--table-sticky-top-effective, var(--table-sticky-top, 0px)) - 16px)",
            }}
          >
            <div style={{ width: topScrollContentWidth, height: 1 }} />
          </div>
        }
        isLoading={isLoading}
        isError={isError}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        totalLabel="Total"
        entityName={pluralize(newsData?.total ?? 0, "novedad", "novedades")}
        tableContainerClassName="overflow-x-auto border rounded-lg"
        showCellBorders={showCellBorders}
      />
    </div>
  );
};
